/* eslint-disable no-param-reassign */
import { createSelector, createSlice } from '@reduxjs/toolkit'

// The REVAMP settings, which is the container this slice's `selectedTab` comes
// from. The legacy module carries only Board / Map / Activity, so every tab added
// since reads as `undefined` off it — which is how the Productivity carve-out in
// `getIsGridDataLoading` came to compare against nothing and strand the page
// loader on. Board, Map, Activity and MASTER_ROUTE_ID hold the same values in both.
import { MASTER_ROUTE_TABS, MASTER_ROUTE_ID } from 'containers/new-route-manager/master-route-revamp/settings'
import { get } from 'utils/lodash'

const { BOARD } = MASTER_ROUTE_TABS

/**
 * Prepopulated payload consumed by `resolveStopsWithPrepopulation` to merge
 * new stops into the fetched grid data, and by the master container to
 * auto-scroll to the just-inserted stop.
 *
 * @typedef {Object} PrePopulatedData
 * @property {string} [resequenceStopId]
 *   CSE ID of an existing stop being resequenced; with `assignSequence` the
 *   transforms move-in-place instead of inserting.
 * @property {number} [assignSequence]
 *   1-based target sequence position.
 * @property {string[]} [sequenceOrder]
 *   Ordered CSE IDs for the master grid (assign/move flows).
 * @property {string[]} [assignedConfiguredServiceEventIds]
 *   Newly-assigned CSE IDs for the master grid.
 * @property {string[]} [assignedDailyConfiguredServiceEventIds]
 *   Same set including on-request stops, for daily grids.
 * @property {string[]} [unassignedConfiguredServiceEventIds]
 * @property {string[]} [unassignedDailyConfiguredServiceEventIds]
 * @property {Object[]} [newMasterStops]
 *   Full stop rows for newly assigned recurring stops — must carry every
 *   field StopCard renders, or the inserted tile renders blank.
 * @property {string[]} [allNewServiceDates]
 *   Daily service dates that need new column grids in the editor.
 * @property {Object<string, Object[]>} [allDailyStopsGrouped]
 *   { serviceDate: previewStops[] } injection map for daily grids.
 * @property {Object<string, number>} [dailyPositionMap]
 *   { workOrderId|cseId: 1-based position } for daily insertion.
 * @property {string} [scrollTargetCseId]
 *   CSE ID the editor auto-scrolls to after grids load.
 */

const MAX_MOVE_UNDO = 25
const VISIBLE_TOAST_CAP = 4

const initialState = {
  selectedTab: BOARD,
  dailySummary: null,
  gridData: {},
  gridDataOriginal: {},
  completionSortByGridId: {},
  unassignedGridData: null,
  // null = the global unassigned pool (default). A route id makes the column a
  // cross-route source: it shows that route's daily stops for the active date
  // so the dispatcher can pull them into the current route (WOLV-4642).
  unassignedSourceRouteId: null,
  applySequenceChanges: false,
  rowSelectionModel: [],
  unassignedRowSelectionModel: [],
  assignedStopsIds: [],
  fitBounds: true,
  isMapViewMaster: true,
  mapViewSelectedDate: '',
  isSequenceLineActive: false,
  // On by default: prior renders only gated on data presence, not these flags.
  isDriverPathActive: true,
  routeDailyGeoJsonId: '',
  breadcrumb: null,
  isContainerLocationActive: true,
  clickedMarkerDetails: null,
  // One-shot pan/zoom target drained by the in-Map effect after panning.
  mapZoomTarget: null,
  lastActivityOrder: 0,
  isUnsavedChangesDialogOpen: false,
  bulkUnassign: { isOpen: false, rowSelectionModel: {} },
  isBulkAssignOpen: false,
  prePopulatedData: null,
  // One-shot board reveal target (configuredServiceEventId) drained by
  // useScrollToSelectedRow after it aligns the just-assigned stop into view.
  boardRevealCseId: null,
  // Board-only week stepper for the history view. 0 = current + upcoming (the
  // normal board). Negative = viewing a past week as read-only history. Reset
  // to 0 on any route/day switch so history never leaks across contexts.
  weekOffset: 0,
  // Promote-daily → master flow: null when closed, else { gridId, step } where
  // step is 'intro' (the pre-flow explainer dialog) or 'preview' (the fullscreen
  // surface — the board unmounts behind it to free memory).
  promotePreview: null,
  moveUndoStack: [],
  moveRedoStack: [],
  visibleToastIds: [],
  branchDialog: { isOpen: false, prefilledName: null, source: null },
  isPublishing: false,
  // Set while the board reloads master/dailies right after a promotion Apply, so
  // the grids show an overlay instead of blanking during the post-publish refetch.
  promotionReloading: false,
  conflictDialog: { isOpen: false },
  // Counter (not boolean) so consecutive "Overwrite anyway" clicks re-fire the consumer effect.
  conflictOverrideTick: 0,
  // Consumed by Branch.jsx on next Live switch to bump versionContext against stale Live.
  liveRefreshPending: false,
  // Fresh BE metadata for each master stop, keyed by configuredServiceEventId.
  // Populated by the master-stops reconcile bridge (useMasterStopsReconcile),
  // which polls a redundant query parked under its own cache tag so it never
  // gets wiped by the publish-path invalidations that refresh gridData. The
  // grid keeps the user's in-flight preview / restore / move state; this map
  // is the side channel cards read for non-volatile metadata (note counts,
  // tags, materials, address edits, deactivated / ended flags).
  reconciledStopsByEventId: {},
}

const masterRouteSlice = createSlice({
  name: 'masterRoute',
  initialState: { ...initialState },
  reducers: {
    setMasterRouteState: (state, action) => {
      return { ...state, ...action.payload }
    },
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload
    },
    setDailySummary: (state, action) => {
      state.dailySummary = action.payload
    },
    setGridData: (state, action) => {
      state.gridData = { ...state.gridData, ...action.payload }
    },
    setGridDataOriginal: (state, action) => {
      state.gridDataOriginal = { ...state.gridDataOriginal, ...action.payload }
    },
    setUnassignedGridData: (state, action) => {
      state.unassignedGridData = action.payload
    },
    setUnassignedSourceRouteId: (state, action) => {
      state.unassignedSourceRouteId = action.payload
    },
    setApplySequenceChanges: (state, action) => {
      state.applySequenceChanges = action.payload
    },
    setRowSelectionModel: (state, action) => {
      state.rowSelectionModel = action.payload
    },
    setBoardRevealCseId: (state, action) => {
      state.boardRevealCseId = action.payload
    },
    setWeekOffset: (state, action) => {
      state.weekOffset = action.payload
    },
    openPromotePreview: (state, action) => {
      state.promotePreview = { gridId: action.payload, step: 'intro', mode: 'consolidate' }
    },
    setCompletionSort: (state, action) => {
      const { gridId, on } = action.payload
      state.completionSortByGridId[gridId] = on
    },
    setPromoteMode: (state, action) => {
      if (state.promotePreview) state.promotePreview.mode = action.payload
    },
    advancePromotePreview: state => {
      if (state.promotePreview) state.promotePreview.step = 'preview'
    },
    closePromotePreview: state => {
      state.promotePreview = null
    },
    setUnassignedRowSelectionModel: (state, action) => {
      state.unassignedRowSelectionModel = action.payload
    },
    setAssignedStopsIds: (state, action) => {
      state.assignedStopsIds = action.payload
    },
    setFitBounds: (state, action) => {
      state.fitBounds = action.payload
    },
    setIsMapViewMaster: (state, action) => {
      state.isMapViewMaster = action.payload
    },
    setMapViewSelectedDate: (state, action) => {
      state.mapViewSelectedDate = action.payload
    },
    setIsSequenceLineActive: (state, action) => {
      state.isSequenceLineActive = action.payload
    },
    setIsDriverPathActive: (state, action) => {
      state.isDriverPathActive = action.payload
    },
    setRouteDailyGeoJsonId: (state, action) => {
      state.routeDailyGeoJsonId = action.payload
    },
    setBreadcrumbState: (state, action) => {
      state.breadcrumb = action.payload
    },
    setIsContainerLocationActive: (state, action) => {
      state.isContainerLocationActive = action.payload
    },
    setClickedMarkerDetails: (state, action) => {
      state.clickedMarkerDetails = action.payload
    },
    /** @param {{ payload: { lat: number, lng: number } | null }} action */
    setMapZoomTarget: (state, action) => {
      state.mapZoomTarget = action.payload
    },
    setLastActivityOrder: (state, action) => {
      state.lastActivityOrder = action.payload
    },
    setUnsavedChangesDialogState: (state, action) => {
      state.isUnsavedChangesDialogOpen = action.payload
    },
    setBulkUnassignState: (state, action) => {
      state.bulkUnassign = action.payload
    },
    setBulkUnassignRowSelectionModel: (state, action) => {
      state.bulkUnassign.rowSelectionModel = { ...state.bulkUnassign.rowSelectionModel, ...action.payload }
    },
    setIsBulkAssignOpen: (state, action) => {
      state.isBulkAssignOpen = action.payload
    },
    /** @param {{ payload: PrePopulatedData | null }} action */
    setPrePopulatedData: (state, action) => {
      state.prePopulatedData = action.payload
    },
    pushMoveSnapshot: (state, action) => {
      const { label = '', gridData = {}, rowSelectionModel = [], lastActivityOrder = 0, formValues = {} } = action.payload ?? {}
      if (!gridData || Object.keys(gridData).length === 0) return
      // assign/unassign also mutate these; capture them so undo restores the
      // full pre-move state, not just the grid rows. Absent on plain drags.
      const { assignedStopsIds, dailySummary } = action.payload ?? {}
      const id = `move-undo-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
      const entry = {
        id,
        label,
        ts: Date.now(),
        gridData,
        rowSelectionModel,
        lastActivityOrder,
        formValues,
        assignedStopsIds,
        dailySummary,
      }
      state.moveUndoStack = [entry, ...state.moveUndoStack].slice(0, MAX_MOVE_UNDO)
      state.moveRedoStack = []
      state.visibleToastIds = [id, ...state.visibleToastIds].slice(0, VISIBLE_TOAST_CAP)
    },
    undoLastMove: (state, action) => {
      const [head, ...rest] = state.moveUndoStack
      if (!head) return
      state.moveUndoStack = rest
      state.gridData = { ...state.gridData, ...head.gridData }
      if (Array.isArray(head.rowSelectionModel)) state.rowSelectionModel = head.rowSelectionModel
      if (typeof head.lastActivityOrder === 'number') state.lastActivityOrder = head.lastActivityOrder
      if (Array.isArray(head.assignedStopsIds)) state.assignedStopsIds = head.assignedStopsIds
      if (Array.isArray(head.dailySummary)) state.dailySummary = head.dailySummary
      state.visibleToastIds = state.visibleToastIds.filter(id => id !== head.id)
      const redoEntry = action.payload?.redoEntry
      if (redoEntry?.gridData && Object.keys(redoEntry.gridData).length > 0) {
        const id = `move-redo-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
        const entry = { id, ts: Date.now(), ...redoEntry }
        state.moveRedoStack = [entry, ...state.moveRedoStack].slice(0, MAX_MOVE_UNDO)
      }
    },
    redoLastMove: (state, action) => {
      const [head, ...rest] = state.moveRedoStack
      if (!head) return
      state.moveRedoStack = rest
      state.gridData = { ...state.gridData, ...head.gridData }
      if (Array.isArray(head.rowSelectionModel)) state.rowSelectionModel = head.rowSelectionModel
      if (typeof head.lastActivityOrder === 'number') state.lastActivityOrder = head.lastActivityOrder
      if (Array.isArray(head.assignedStopsIds)) state.assignedStopsIds = head.assignedStopsIds
      if (Array.isArray(head.dailySummary)) state.dailySummary = head.dailySummary
      const undoEntry = action.payload?.undoEntry
      if (undoEntry?.gridData && Object.keys(undoEntry.gridData).length > 0) {
        const id = `move-undo-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
        const entry = { id, ts: Date.now(), ...undoEntry }
        state.moveUndoStack = [entry, ...state.moveUndoStack].slice(0, MAX_MOVE_UNDO)
      }
    },
    undoEntryById: (state, action) => {
      const targetId = action.payload?.id
      if (!targetId) return
      const targetIdx = state.moveUndoStack.findIndex(e => e.id === targetId)
      if (targetIdx === -1) return
      const toPop = state.moveUndoStack.slice(0, targetIdx + 1)
      const popIds = new Set(toPop.map(e => e.id))
      // Newest-first so each redo entry captures the gridData state right
      // BEFORE that pop applies its restore.
      toPop.forEach(entry => {
        const touchedKeys = Object.keys(entry.gridData ?? {})
        const redoGridData = touchedKeys.reduce((acc, k) => {
          acc[k] = state.gridData[k]
          return acc
        }, {})
        const redoId = `move-redo-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
        const redoEntry = {
          id: redoId,
          ts: Date.now(),
          label: entry.label,
          gridData: redoGridData,
          rowSelectionModel: state.rowSelectionModel,
          lastActivityOrder: state.lastActivityOrder,
          formValues: entry.formValues,
          assignedStopsIds: state.assignedStopsIds,
          dailySummary: state.dailySummary,
        }
        state.moveRedoStack = [redoEntry, ...state.moveRedoStack].slice(0, MAX_MOVE_UNDO)
        state.gridData = { ...state.gridData, ...entry.gridData }
        if (Array.isArray(entry.rowSelectionModel)) state.rowSelectionModel = entry.rowSelectionModel
        if (typeof entry.lastActivityOrder === 'number') state.lastActivityOrder = entry.lastActivityOrder
        if (Array.isArray(entry.assignedStopsIds)) state.assignedStopsIds = entry.assignedStopsIds
        if (Array.isArray(entry.dailySummary)) state.dailySummary = entry.dailySummary
      })
      state.moveUndoStack = state.moveUndoStack.slice(targetIdx + 1)
      state.visibleToastIds = state.visibleToastIds.filter(id => !popIds.has(id))
    },
    dismissMoveToast: (state, action) => {
      const { id } = action.payload ?? {}
      if (!id) return
      state.visibleToastIds = state.visibleToastIds.filter(x => x !== id)
    },
    clearMoveUndoStack: state => {
      state.moveUndoStack = []
      state.moveRedoStack = []
      state.visibleToastIds = []
    },
    openBranchDialog: (state, action) => {
      const payload = action.payload || {}
      state.branchDialog = {
        isOpen: true,
        prefilledName: payload.prefilledName ?? null,
        source: payload.source ?? null,
      }
    },
    closeBranchDialog: state => {
      state.branchDialog = { isOpen: false, prefilledName: null, source: null }
    },
    setIsPublishing: (state, action) => {
      state.isPublishing = Boolean(action.payload)
    },
    setPromotionReloading: (state, action) => {
      state.promotionReloading = Boolean(action.payload)
    },
    openConflictDialog: state => {
      state.conflictDialog = { isOpen: true }
    },
    closeConflictDialog: state => {
      state.conflictDialog = { isOpen: false }
    },
    markLiveRefreshPending: state => {
      state.liveRefreshPending = true
    },
    clearLiveRefreshPending: state => {
      state.liveRefreshPending = false
    },
    requestConflictOverride: state => {
      state.conflictOverrideTick += 1
    },
    setReconciledStopsByEventId: (state, action) => {
      state.reconciledStopsByEventId = action.payload || {}
    },
    resetRouteAndDayChange: (state, action) => {
      // preserveTab is set when only `versionContext` bumped (Discard & reload):
      // route/day didn't change, so we keep the user on their current tab.
      // dailySummary/gridData still get wiped so each DataGridContainer's
      // `!rows` effect re-triggers and pulls fresh data — that's the actual
      // refresh mechanism (tag invalidation alone doesn't re-fire a lazy query).
      // The page-level loader stays correct because `getIsGridDataLoading` is
      // tab-aware: on Map only the active grid is required.
      const preserveTab = get(action, 'payload.preserveTab', false)
      return {
        ...state,
        selectedTab: preserveTab ? state.selectedTab : BOARD,
        dailySummary: null,
        gridData: {},
        gridDataOriginal: {},
        // Switching route/day drops the cross-route source picker back to the
        // unassigned pool; a stale source route from the prior route is meaningless.
        // Clear the loaded grid + its selection too, otherwise the column keeps
        // showing the prior source route's stops (the fetch effect won't re-run
        // while `unassignedGridData` is non-null) while the picker reads "Unassigned".
        unassignedSourceRouteId: null,
        unassignedGridData: null,
        unassignedRowSelectionModel: [],
        rowSelectionModel: [],
        fitBounds: true,
        isMapViewMaster: true,
        mapViewSelectedDate: '',
        lastActivityOrder: 0,
        moveUndoStack: [],
        moveRedoStack: [],
        visibleToastIds: [],
        // Exit history when the route/day changes — a past offset from the
        // prior context would silently mislabel the new one.
        weekOffset: 0,
      }
    },
    resetUnassignedState: (state, action) => {
      state.unassignedRowSelectionModel = []
      state.assignedStopsIds = []
      state.isBulkAssignOpen = false
      if (get(action, 'payload.resetData', false)) {
        state.unassignedGridData = null
      }
    },
    resetMasterRouteState: state => {
      return { ...initialState, prePopulatedData: state.prePopulatedData }
    },
  },
})

export const {
  setMasterRouteState,
  setSelectedTab,
  setDailySummary,
  setGridData,
  setGridDataOriginal,
  setUnassignedGridData,
  setUnassignedSourceRouteId,
  setApplySequenceChanges,
  setRowSelectionModel,
  setBoardRevealCseId,
  setWeekOffset,
  openPromotePreview,
  setCompletionSort,
  setPromoteMode,
  advancePromotePreview,
  closePromotePreview,
  setUnassignedRowSelectionModel,
  setAssignedStopsIds,
  setFitBounds,
  setIsMapViewMaster,
  setMapViewSelectedDate,
  setIsSequenceLineActive,
  setIsDriverPathActive,
  setRouteDailyGeoJsonId,
  setBreadcrumbState,
  setIsContainerLocationActive,
  setClickedMarkerDetails,
  setMapZoomTarget,
  setLastActivityOrder,
  setUnsavedChangesDialogState,
  setBulkUnassignState,
  setBulkUnassignRowSelectionModel,
  setIsBulkAssignOpen,
  resetRouteAndDayChange,
  resetUnassignedState,
  resetMasterRouteState,
  setPrePopulatedData,
  pushMoveSnapshot,
  undoLastMove,
  redoLastMove,
  undoEntryById,
  dismissMoveToast,
  clearMoveUndoStack,
  openBranchDialog,
  closeBranchDialog,
  setIsPublishing,
  setPromotionReloading,
  openConflictDialog,
  closeConflictDialog,
  markLiveRefreshPending,
  clearLiveRefreshPending,
  requestConflictOverride,
  setReconciledStopsByEventId,
} = masterRouteSlice.actions

export const selectSelectedTab = s => get(s, ['masterRoute', 'selectedTab'])
export const selectDailySummary = s => get(s, ['masterRoute', 'dailySummary'], [])
export const selectGridData = s => get(s, ['masterRoute', 'gridData'])
export const selectGridDataOriginal = s => get(s, ['masterRoute', 'gridDataOriginal'])
export const selectUnassignedGridData = s => get(s, ['masterRoute', 'unassignedGridData'])
export const selectUnassignedSourceRouteId = s => get(s, ['masterRoute', 'unassignedSourceRouteId'], null)
export const selectApplySequenceChanges = s => get(s, ['masterRoute', 'applySequenceChanges'])
export const selectRowSelectionModel = s => get(s, ['masterRoute', 'rowSelectionModel'], [])
export const selectBoardRevealCseId = s => get(s, ['masterRoute', 'boardRevealCseId'], null)
export const selectWeekOffset = s => get(s, ['masterRoute', 'weekOffset'], 0)
export const selectCompletionSort = gridId => s => Boolean(get(s, ['masterRoute', 'completionSortByGridId', gridId]))
export const selectPromotePreview = s => get(s, ['masterRoute', 'promotePreview'], null)
// Keystone read-only gate: a negative week offset means the board is showing a
// past week as history. Every board mutation (DnD, assign, unassign, cascade,
// fan-out) must consult this and refuse to mutate. Promotion-to-master is the
// one deliberate exception, guarded separately on in-flight state.
export const selectIsBoardHistory = s => selectWeekOffset(s) < 0
// Memoized set of the current master's stop CSE ids — used in the history view
// to flag which past-day stops would (not) carry over on a promote to master.
export const selectMasterStopCseIdSet = createSelector(
  s => get(s, `masterRoute.gridData.${MASTER_ROUTE_ID}`, []),
  rows => new Set((rows || []).map(row => get(row, 'configuredServiceEventId')))
)
export const selectUnassignedRowSelectionModel = s => get(s, ['masterRoute', 'unassignedRowSelectionModel'], [])
export const selectAssignedStopsIds = s => get(s, ['masterRoute', 'assignedStopsIds'], [])
export const selectFitBounds = s => get(s, ['masterRoute', 'fitBounds'])
export const selectIsMapViewMaster = s => get(s, ['masterRoute', 'isMapViewMaster'])
export const selectMapViewSelectedDate = s => get(s, ['masterRoute', 'mapViewSelectedDate'])
export const selectIsSequenceLineActive = s => get(s, ['masterRoute', 'isSequenceLineActive'])
export const selectIsDriverPathActive = s => get(s, ['masterRoute', 'isDriverPathActive'])
export const selectRouteDailyGeoJsonId = s => get(s, ['masterRoute', 'routeDailyGeoJsonId'])
export const selectIsContainerLocationActive = s => get(s, ['masterRoute', 'isContainerLocationActive'])
export const selectBreadcrumb = s => get(s, ['masterRoute', 'breadcrumb'])
export const selectClickedMarkerDetails = s => get(s, ['masterRoute', 'clickedMarkerDetails'])
export const selectMapZoomTarget = s => get(s, ['masterRoute', 'mapZoomTarget'])
export const selectLastActivityOrder = s => get(s, ['masterRoute', 'lastActivityOrder'])
export const selectIsUnsavedChangesDialogOpen = s => get(s, ['masterRoute', 'isUnsavedChangesDialogOpen'])
export const selectBulkUnassign = s => get(s, ['masterRoute', 'bulkUnassign'])
export const selectIsBulkAssignOpen = s => get(s, ['masterRoute', 'isBulkAssignOpen'])
export const selectPrePopulatedData = s => get(s, ['masterRoute', 'prePopulatedData'], null)
export const selectMoveUndoStack = s => get(s, ['masterRoute', 'moveUndoStack'], [])
export const selectMoveRedoStack = s => get(s, ['masterRoute', 'moveRedoStack'], [])
export const selectVisibleToastIds = s => get(s, ['masterRoute', 'visibleToastIds'], [])
export const selectCanUndoMove = s => (get(s, ['masterRoute', 'moveUndoStack'], []) ?? []).length > 0
export const selectCanRedoMove = s => (get(s, ['masterRoute', 'moveRedoStack'], []) ?? []).length > 0
export const selectBranchDialog = s => get(s, ['masterRoute', 'branchDialog'], { isOpen: false, prefilledName: null })
export const selectIsPublishing = s => Boolean(get(s, ['masterRoute', 'isPublishing'], false))
export const selectPromotionReloading = s => Boolean(get(s, ['masterRoute', 'promotionReloading'], false))
export const selectConflictDialog = s => get(s, ['masterRoute', 'conflictDialog'], { isOpen: false })
export const selectLiveRefreshPending = s => Boolean(get(s, ['masterRoute', 'liveRefreshPending'], false))
export const selectConflictOverrideTick = s => get(s, ['masterRoute', 'conflictOverrideTick'], 0)
export const selectReconciledStopsByEventId = s => get(s, ['masterRoute', 'reconciledStopsByEventId'], {})

export const getIsGridDataLoading = s => {
  const selectedTab = s?.masterRoute?.selectedTab

  // Productivity renders from its own per-day fetches and mounts no board grid,
  // so gridData never fills in there and the page loader would never clear.
  if (selectedTab === MASTER_ROUTE_TABS.PRODUCTIVITY) return false

  const gridData = get(s, 'masterRoute.gridData', {})
  const dailySummary = get(s, ['masterRoute', 'dailySummary'])
  if (!dailySummary) return true

  // Map tab mounts only the active grid (master OR selected daily), so the
  // Board-style "all dailies must be loaded" check would strand it in a loader.
  if (selectedTab === MASTER_ROUTE_TABS.MAP) {
    const isMapViewMaster = get(s, ['masterRoute', 'isMapViewMaster'])
    const mapViewSelectedDate = get(s, ['masterRoute', 'mapViewSelectedDate'])
    const activeGridId = isMapViewMaster ? MASTER_ROUTE_ID : mapViewSelectedDate
    if (!activeGridId) return false
    return !gridData[activeGridId]
  }

  // Master is optional (withMaster off skips its fetch), so check each daily explicitly.
  return dailySummary.some(d => !gridData[d.serviceDate])
}

export default masterRouteSlice.reducer
